import { getSpecificParent } from '@apia/util';
import uniqueId from 'lodash-es/uniqueId';
import React from 'react';

/**
 * Este hook está pensado para crear un botón utilizando un tag cualquiera.
 *
 * @example
 *
 * const buttonProps = useOtherTagButton(React.useMemo(()=>({
 *   onClick(ev) { console.log(ev.target) },
 *   onKeyDown(ev) { console.log(ev.code) }
 * }),[])
 *
 * return <div {...buttonProps}>Esto es un botón</div>
 */
export function useOtherTagButton(
  props?: Partial<{
    className?: string;
    onClick: React.MouseEventHandler;
    onKeyDown: React.KeyboardEventHandler;
  }>,
) {
  const specificClass = React.useMemo(() => `otherTag${uniqueId()}`, []);

  const actualOnClick = React.useCallback(
    (ev: React.MouseEvent) => {
      props?.onClick?.(ev);
    },
    [props],
  );
  const actualOnKeyDown = React.useCallback(
    (ev: React.KeyboardEvent) => {
      props?.onKeyDown?.(ev);
      if (ev.code === 'Enter' || ev.code === 'Space') {
        ev.preventDefault();
        const button = getSpecificParent(ev.target as HTMLElement, (current) =>
          current.classList.contains(specificClass),
        );
        if (button) button.click();
      }
    },
    [props, specificClass],
  );

  return {
    className: `${specificClass} ${props?.className ?? ''}`,
    onClick: actualOnClick,
    onKeyDown: actualOnKeyDown,
    role: 'button',
    tabIndex: 0,
  };
}
